import React, { useState, useEffect } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Shield,
  Users,
  MessageSquare,
  QrCode,
  Settings,
  Activity,
  LogOut,
  Search,
  Eye,
  EyeOff,
  Save,
  Crown,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Plus,
  Trash2,
  Edit,
  Mail,
  Phone,
  Database,
  Download,
  Upload,
  AlertTriangle,
  Package,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function AdminPanel() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Handle logout with navigation
  const handleLogout = () => {
    logout();
    // Use window.location for clean redirect without React state conflicts
    window.location.href = '/';
  };
  
  // Admin data states
  const [adminStats, setAdminStats] = useState({});
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [systemSettings, setSystemSettings] = useState({});
  const [userSearch, setUserSearch] = useState('');
  const [messageSearch, setMessageSearch] = useState('');
  const [showApiKeys, setShowApiKeys] = useState(false);
  
  // Pagination
  const [userPage, setUserPage] = useState(1);
  const [messagePage, setMessagePage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  
  // QR Batches
  const [qrBatches, setQrBatches] = useState([]);
  const [showBulkGenerateDialog, setShowBulkGenerateDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchCodes, setBatchCodes] = useState([]);
  
  // Products
  const [products, setProducts] = useState([]);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSortBy, setProductSortBy] = useState('name'); // 'name', 'price', 'stock', 'created'
  const [productSortOrder, setProductSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Orders
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'stickers',
    qr_codes_included: '8',
    stock_quantity: '',
    is_active: true,
    images: []
  });
  
  // Backup & Restore
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);

  // Define all functions before useEffect to avoid hoisting issues
  const fetchAdminStats = async () => {
    setLoading(true);
    try {
      console.log('Fetching admin stats...');
      const response = await api.getAdminStats();
      console.log('Admin stats response:', response.data);
      setAdminStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminUsers(userPage, 20, userSearch);
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminMessages(messagePage, 20, messageSearch);
      setMessages(response.data.messages);
      setTotalMessages(response.data.total);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchQRBatches = async () => {
    setLoading(true);
    try {
      const response = await api.getQRBatches();
      setQrBatches(response.data.batches);
    } catch (error) {
      console.error('Failed to fetch QR batches:', error);
      toast.error('Failed to load QR batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminProducts();
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await api.getOrders();
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  };
  
  const handleMarkAsShipped = async (orderId) => {
    try {
      await api.updateOrderStatus(orderId, { status: 'shipped' });
      toast.success('Order marked as shipped!');
      fetchOrders();
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order status');
    }
  };
  
  const handleCopyAddress = (order) => {
    const addr = order.shipping_address || {};
    const addressText = `${addr.name || ''}\n${addr.address_line1 || ''}\n${addr.address_line2 || ''}\n${addr.city || ''}, ${addr.state || ''} ${addr.zip_code || ''}`.trim();
    navigator.clipboard.writeText(addressText);
    toast.success('Address copied to clipboard!');
  };
  
  const handleDownloadLabel = async (orderId) => {
    try {
      const response = await api.getShippingLabel(orderId);
      const pdfData = atob(response.data.label_pdf);
      const bytes = new Uint8Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        bytes[i] = pdfData.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `shipping-label-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Shipping label downloaded!');
    } catch (error) {
      console.error('Failed to download label:', error);
      toast.error('Failed to generate shipping label');
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await api.getSystemSettings();
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load system settings');
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      await api.toggleUserStatus(userId);
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleBulkGenerate = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    
    const bulkData = {
      quantity: parseInt(formData.get('quantity')),
      purpose: formData.get('purpose'),
      notes: formData.get('notes')
    };

    try {
      setLoading(true);
      const response = await api.bulkGenerateQR(bulkData);
      toast.success(`Generated ${bulkData.quantity} QR codes successfully!`);
      setShowBulkGenerateDialog(false);
      fetchQRBatches();
      event.target.reset();
    } catch (error) {
      console.error('Failed to generate QR codes:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate QR codes');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBatchCodes = async (batchId) => {
    try {
      setLoading(true);
      const response = await api.getBatchQRCodes(batchId);
      setBatchCodes(response.data.codes);
      setSelectedBatch(response.data.batch_info);
    } catch (error) {
      console.error('Failed to fetch batch codes:', error);
      toast.error('Failed to load batch codes');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPrinted = async (batchId) => {
    try {
      await api.markBatchPrinted(batchId);
      toast.success('Batch marked as printed');
      fetchQRBatches();
    } catch (error) {
      console.error('Failed to mark batch as printed:', error);
      toast.error('Failed to mark batch as printed');
    }
  };

  const handleDownloadCSV = async (batchId, batchPurpose) => {
    try {
      setLoading(true);
      const response = await api.downloadBatchCSV(batchId);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qr-codes-${batchPurpose.replace('_', '-')}-${batchId.slice(0, 8)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV file downloaded successfully');
    } catch (error) {
      console.error('Failed to download CSV:', error);
      toast.error('Failed to download CSV file');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Product management functions

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      qr_codes_included: product.qr_codes_included.toString(),
      stock_quantity: product.stock_quantity ? product.stock_quantity.toString() : '',
      is_active: product.is_active,
      images: product.images || []
    });
    setShowProductDialog(true);
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      category: 'stickers',
      qr_codes_included: '8',
      stock_quantity: '',
      is_active: true,
      images: []
    });
    setShowProductDialog(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Image must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductForm({
          ...productForm,
          images: [reader.result] // Store as base64
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProductForm({
      ...productForm,
      images: []
    });
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        qr_codes_included: parseInt(productForm.qr_codes_included),
        stock_quantity: productForm.stock_quantity ? parseInt(productForm.stock_quantity) : null
      };

      if (editingProduct) {
        await api.updateProduct(editingProduct.id, productData);
        toast.success('Product updated successfully!');
      } else {
        await api.createProduct(productData);
        toast.success('Product created successfully!');
      }

      setShowProductDialog(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.deleteProduct(productId);
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  // Filter and sort products
  const getFilteredAndSortedProducts = () => {
    let filtered = products.filter(product => 
      product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      product.category.toLowerCase().includes(productSearchTerm.toLowerCase())
    );

    // Sort products
    filtered.sort((a, b) => {
      let compareA, compareB;
      
      switch (productSortBy) {
        case 'name':
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
          break;
        case 'price':
          compareA = parseFloat(a.price);
          compareB = parseFloat(b.price);
          break;
        case 'stock':
          compareA = a.stock_quantity || 0;
          compareB = b.stock_quantity || 0;
          break;
        case 'created':
          compareA = new Date(a.created_at || 0);
          compareB = new Date(b.created_at || 0);
          break;
        default:
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
      }

      if (productSortOrder === 'asc') {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    });

    return filtered;
  };


  // Backup & Restore functions
  const fetchBackupInfo = async () => {
    try {
      const response = await api.getBackupInfo();
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch backup info:', error);
      toast.error('Failed to fetch backup info');
    }
  };

  const handleDownloadBackup = async () => {
    try {
      setBackupLoading(true);
      const response = await api.downloadDatabaseBackup();
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute('download', `icomment_backup_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Database backup downloaded successfully!');
    } catch (error) {
      console.error('Failed to download backup:', error);
      toast.error('Failed to download backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file first');
      return;
    }

    if (!confirm('⚠️ WARNING: This will restore the database from the backup file. This action cannot be undone. Continue?')) {
      return;
    }

    try {
      setBackupLoading(true);
      const response = await api.restoreDatabaseBackup(restoreFile);
      
      toast.success(`Database restored successfully! ${response.data.restored_collections ? Object.keys(response.data.restored_collections).length : 0} collections restored.`);
      setRestoreFile(null);
      
      // Refresh data
      fetchBackupInfo();
      if (activeTab === 'dashboard') fetchAdminStats();
    } catch (error) {
      console.error('Failed to restore backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Please select a valid JSON backup file');
        return;
      }
      setRestoreFile(file);
      toast.info(`File selected: ${file.name}`);
    }
  };


  // useEffect after all functions are declared
  useEffect(() => {
    // CRITICAL FIX: Only make API calls if user is authenticated and is admin
    // This prevents 403 errors when unauthenticated users briefly mount AdminPanel
    if (!authLoading && user && user.role === 'admin') {
      if (activeTab === 'dashboard') {
        fetchAdminStats();
      } else if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'orders') {
        fetchOrders();
      } else if (activeTab === 'qr-codes') {
        fetchQRBatches();
      } else if (activeTab === 'products') {
        fetchProducts();
      } else if (activeTab === 'settings') {
        fetchBackupInfo();
      } else if (activeTab === 'messages') {
        fetchMessages();
      } else if (activeTab === 'settings') {
        fetchSystemSettings();
      }
    }
  }, [activeTab, userPage, messagePage, authLoading, user]);

  // Show loading spinner while authentication is being verified
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect if not admin
  if (user && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-2">
                <Shield className="h-8 w-8 text-red-600" />
                <span className="text-2xl font-bold text-gray-900">iComment Admin</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-red-600 text-white" data-testid="admin-badge">
                <Crown className="h-3 w-3 mr-1" />
                Administrator
              </Badge>
              <span className="text-sm text-gray-600" data-testid="admin-email">
                {user?.email}
              </span>
              <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 max-w-5xl text-xs">
            <TabsTrigger value="dashboard" data-testid="admin-dashboard-tab" className="text-xs">
              <Activity className="h-3 w-3 mr-1" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-users-tab" className="text-xs">
              <Users className="h-3 w-3 mr-1" />
              Users ({totalUsers})
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-orders-tab" className="text-xs">
              <Package className="h-3 w-3 mr-1" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="qr-codes" data-testid="admin-qr-tab" className="text-xs">
              <QrCode className="h-3 w-3 mr-1" />
              QR Batches
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-products-tab" className="text-xs">
              <Package className="h-3 w-3 mr-1" />
              Products
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-messages-tab" className="text-xs">
              <MessageSquare className="h-3 w-3 mr-1" />
              Messages ({totalMessages})
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-settings-tab" className="text-xs">
              <Settings className="h-3 w-3 mr-1" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards with Refresh Button */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
              <Button 
                onClick={fetchAdminStats} 
                disabled={loading}
                variant="outline"
                size="sm"
              >
                {loading ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full mr-2"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <Activity className="h-4 w-4 mr-2" />
                    Refresh Stats
                  </>
                )}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="admin-stats">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total Users</p>
                      <p className="text-3xl font-bold text-blue-900">{adminStats.total_users || 0}</p>
                      <p className="text-xs text-blue-600">+{adminStats.users_last_30_days || 0} this month</p>
                    </div>
                    <Users className="h-12 w-12 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Total Messages</p>
                      <p className="text-3xl font-bold text-green-900">{adminStats.total_messages || 0}</p>
                      <p className="text-xs text-green-600">+{adminStats.messages_last_30_days || 0} this month</p>
                    </div>
                    <MessageSquare className="h-12 w-12 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">QR Codes</p>
                      <p className="text-3xl font-bold text-purple-900">{adminStats.total_qr_codes || 0}</p>
                      <p className="text-xs text-purple-600">{adminStats.active_codes || 0} active</p>
                    </div>
                    <QrCode className="h-12 w-12 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600">Growth</p>
                      <p className="text-3xl font-bold text-orange-900">📈</p>
                      <p className="text-xs text-orange-600">System healthy</p>
                    </div>
                    <TrendingUp className="h-12 w-12 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subscription Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Subscription Breakdown</CardTitle>
                <CardDescription>Distribution of user subscription plans</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(adminStats.subscription_breakdown || {}).map(([plan, count]) => (
                    <div key={plan} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{count}</p>
                      <p className="text-sm text-gray-600 capitalize">{plan} Plan</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>Manage all registered users</CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-64"
                      data-testid="user-search"
                    />
                    <Button onClick={fetchUsers} data-testid="search-users-btn">Search</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p>Loading users...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {users.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`user-${user.id}`}>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <div>
                              <p className="font-medium text-gray-900">{user.email || user.username}</p>
                              <p className="text-sm text-gray-600">
                                {user.role} • {user.subscription_plan} • 
                                QR: {user.qr_codes_used} • Messages: {user.messages_used}
                              </p>
                              <p className="text-xs text-gray-500">Joined: {formatDate(user.created_at)}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={user.is_active ? "default" : "destructive"}>
                            {user.is_active ? "Active" : "Disabled"}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingUser(user);
                              setShowEditUserDialog(true);
                            }}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleUserStatus(user.id)}
                            data-testid={`toggle-user-${user.id}`}
                          >
                            {user.is_active ? "Disable" : "Enable"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Order Management</CardTitle>
                  <Button onClick={fetchOrders} variant="outline" size="sm">
                    <Activity className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
                <CardDescription>
                  Manage and fulfill physical product orders
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingOrders ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p>Loading orders...</p>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No orders yet</p>
                    <p className="text-sm text-gray-500">Orders will appear here when customers purchase physical products</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border rounded-lg p-4 bg-gray-50" data-testid={`order-${order.id}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="flex items-center space-x-3 mb-2">
                              <Badge variant={
                                order.order_status === 'pending' ? 'default' :
                                order.order_status === 'shipped' ? 'secondary' :
                                'outline'
                              }>
                                {order.order_status || 'pending'}
                              </Badge>
                              <span className="text-sm font-mono text-gray-600">#{order.id.substring(0, 8)}</span>
                              <span className="text-sm text-gray-500">
                                {new Date(order.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="text-sm">
                              <span className="font-medium">Customer:</span> {order.user_email || 'Unknown'}
                            </div>
                            <div className="text-sm">
                              <span className="font-medium">Total:</span> ${order.total_amount?.toFixed(2)}
                            </div>
                          </div>
                        </div>

                        {/* Products in Order */}
                        <div className="mb-4 bg-white p-3 rounded border">
                          <div className="text-sm font-medium mb-2">Items:</div>
                          <div className="space-y-2">
                            {order.products?.map((item, idx) => (
                              <div key={idx} className="flex items-center space-x-3">
                                {item.product_image && (
                                  <img src={item.product_image} alt={item.product_name} className="w-10 h-10 object-cover rounded" />
                                )}
                                <div className="flex-1 text-sm">
                                  <div className="font-medium">{item.product_name || 'Product'}</div>
                                  <div className="text-gray-600">Qty: {item.quantity} × ${item.price?.toFixed(2)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Shipping Address */}
                        {order.shipping_address && (
                          <div className="mb-4 bg-white p-3 rounded border">
                            <div className="text-sm font-medium mb-2">Ship To:</div>
                            <div className="text-sm space-y-1">
                              <div className="font-medium">{order.shipping_address.name}</div>
                              <div>{order.shipping_address.address_line1}</div>
                              {order.shipping_address.address_line2 && (
                                <div>{order.shipping_address.address_line2}</div>
                              )}
                              <div>
                                {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleCopyAddress(order)}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Address
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDownloadLabel(order.id)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download 4x6 Label
                          </Button>
                          {order.order_status !== 'shipped' && (
                            <Button 
                              size="sm"
                              onClick={() => handleMarkAsShipped(order.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark as Shipped
                            </Button>
                          )}
                        </div>

                        {order.tracking_number && (
                          <div className="mt-3 text-sm text-gray-600">
                            <span className="font-medium">Tracking:</span> {order.tracking_number}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* QR Batches Tab */}
          <TabsContent value="qr-codes" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>QR Code Batch Management</CardTitle>
                    <CardDescription>Generate and manage QR codes for sticker printing and user purchases</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowBulkGenerateDialog(true)} 
                    data-testid="bulk-generate-btn"
                    disabled={loading}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Generate Batch
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p>Loading batches...</p>
                  </div>
                ) : qrBatches.length === 0 ? (
                  <div className="text-center py-8">
                    <QrCode className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No QR code batches yet</p>
                    <p className="text-sm text-gray-500">Generate your first batch of QR codes for printing</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {qrBatches.map((batch) => (
                      <div key={batch.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`batch-${batch.id}`}>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${batch.status === 'printed' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {batch.quantity} QR Codes - {batch.purpose.replace('_', ' ')}
                              </p>
                              <p className="text-sm text-gray-600">
                                Generated: {formatDate(batch.created_at)} • 
                                Claimed: {batch.codes_claimed || 0} • 
                                Available: {batch.codes_unclaimed || 0}
                              </p>
                              {batch.notes && (
                                <p className="text-xs text-gray-500 mt-1">{batch.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={batch.status === 'printed' ? "default" : "secondary"}>
                            {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewBatchCodes(batch.id)}
                            data-testid={`view-codes-${batch.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Codes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadCSV(batch.id, batch.purpose)}
                            data-testid={`download-csv-${batch.id}`}
                            disabled={loading}
                          >
                            📊 CSV
                          </Button>
                          {batch.status !== 'printed' && (
                            <Button
                              size="sm"
                              onClick={() => handleMarkPrinted(batch.id)}
                              data-testid={`mark-printed-${batch.id}`}
                            >
                              Mark Printed
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Batch Codes Display */}
            {selectedBatch && batchCodes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Batch Codes - {selectedBatch.purpose.replace('_', ' ')}</CardTitle>
                  <CardDescription>
                    {batchCodes.length} QR codes ready for printing • Generated: {formatDate(selectedBatch.created_at)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-h-96 overflow-y-auto">
                    {batchCodes.map((code) => (
                      <div key={code.uid} className="text-center p-3 border rounded-lg" data-testid={`code-${code.uid}`}>
                        <img 
                          src={code.qr_image} 
                          alt={`QR Code ${code.uid}`}
                          className="w-20 h-20 mx-auto mb-2"
                        />
                        <p className="text-sm font-mono">{code.uid}</p>
                        <p className="text-xs text-gray-500">
                          {code.is_claimed ? (
                            <Badge variant="secondary" className="text-xs">Claimed</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Available</Badge>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Physical Products Management</CardTitle>
                    <CardDescription>Manage stickers, cards, keychains and bundles in the storefront</CardDescription>
                  </div>
                  <Button 
                    onClick={handleCreateProduct} 
                    data-testid="add-product-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Product
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search and Sort Controls */}
                <div className="flex gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search products by name, description, or category..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={productSortBy}
                      onChange={(e) => setProductSortBy(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="name">Sort by Name</option>
                      <option value="price">Sort by Price</option>
                      <option value="stock">Sort by Stock</option>
                      <option value="created">Sort by Date</option>
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setProductSortOrder(productSortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                      {productSortOrder === 'asc' ? '↑' : '↓'}
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p>Loading products...</p>
                  </div>
                ) : getFilteredAndSortedProducts().length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">
                      {productSearchTerm ? 'No products found matching your search' : 'No products yet'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {productSearchTerm ? 'Try a different search term' : 'Add your first physical product to the storefront'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getFilteredAndSortedProducts().map((product) => (
                      <div key={product.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`product-${product.id}`}>
                        <div className="flex items-center space-x-4 flex-1">
                          {/* Product Image Thumbnail */}
                          <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                            {product.images && product.images.length > 0 ? (
                              <img 
                                src={product.images[0]} 
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <Package className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                          
                          {/* Product Details */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className={`w-3 h-3 rounded-full ${product.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {product.name} - ${product.price}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {product.product_type === 'digital' ? '📱 Digital' : '📦 Physical'} • {product.category} • {product.qr_codes_included} QR codes • 
                                  {product.stock_quantity ? ` ${product.stock_quantity} in stock` : ' Unlimited'}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">{product.description.substring(0, 100)}...</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={product.is_active ? "default" : "secondary"}>
                            {product.is_active ? "Active" : "Inactive"}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditProduct(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Message Management</CardTitle>
                <CardDescription>Monitor all messages in the system</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search Input */}
                <div className="mb-6 flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search messages by keyword, sender name, or QR code..."
                      value={messageSearch}
                      onChange={(e) => setMessageSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      setMessagePage(1);
                      fetchMessages();
                    }}
                    disabled={loading}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  {messageSearch && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setMessageSearch('');
                        setMessagePage(1);
                        setTimeout(() => fetchMessages(), 100);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p>Loading messages...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Search Results Info */}
                    {messageSearch && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-900">
                          {totalMessages > 0 ? (
                            <>Found <strong>{totalMessages}</strong> message{totalMessages !== 1 ? 's' : ''} matching "{messageSearch}"</>
                          ) : (
                            <>No messages found matching "{messageSearch}"</>
                          )}
                        </p>
                      </div>
                    )}
                    
                    {messages.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>{messageSearch ? 'No messages found matching your search.' : 'No messages yet.'}</p>
                      </div>
                    ) : (
                      messages.map((message) => (
                      <div key={message.id} className="p-4 bg-gray-50 rounded-lg" data-testid={`message-${message.id}`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-2">
                            <Badge variant={message.sender_type === 'finder' ? 'default' : 'secondary'}>
                              {message.sender_type}
                            </Badge>
                            {message.qr_code && (
                              <Badge variant="outline">
                                {message.qr_code.title} ({message.qr_code.uid})
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{formatDate(message.created_at)}</p>
                        </div>
                        <p className="text-gray-900 mb-2">{message.content}</p>
                        {(message.sender_email || message.sender_phone || message.location_data) && (
                          <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                            <p className="text-amber-900 font-semibold text-xs mb-2">
                              ⚠️ Compliance/Legal Access Only
                            </p>
                            {message.sender_email && (
                              <p className="flex items-center">
                                <Mail className="h-3 w-3 mr-2 text-gray-600" />
                                <span className="text-gray-700">{message.sender_email}</span>
                              </p>
                            )}
                            {message.sender_phone && (
                              <p className="flex items-center">
                                <Phone className="h-3 w-3 mr-2 text-gray-600" />
                                <span className="text-gray-700">{message.sender_phone}</span>
                              </p>
                            )}
                            {message.location_data && <p>📍 Location shared</p>}
                          </div>
                        )}
                      </div>
                    ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">

            {/* API & Integration Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  API & Integration Settings
                </CardTitle>
                <CardDescription>
                  Configure third-party API credentials for SMS and email services
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const settings = {
                    sendgrid_api_key: formData.get('sendgrid_api_key'),
                    twilio_account_sid: formData.get('twilio_account_sid'),
                    twilio_auth_token: formData.get('twilio_auth_token'),
                    twilio_phone_number: formData.get('twilio_phone_number'),
                    notification_mode: formData.get('notification_mode')
                  };
                  
                  try {
                    await api.updateSystemSettings(settings);
                    toast.success('Settings updated successfully!');
                    fetchSystemSettings();
                  } catch (error) {
                    console.error('Failed to update settings:', error);
                    toast.error(error.response?.data?.detail || 'Failed to update settings');
                  }
                }}>
                  {/* SendGrid Settings */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      SendGrid (Email Service)
                    </h4>
                    <div>
                      <Label htmlFor="sendgrid_api_key">SendGrid API Key</Label>
                      <div className="flex space-x-2 mt-1">
                        <Input
                          id="sendgrid_api_key"
                          name="sendgrid_api_key"
                          type={showApiKeys ? 'text' : 'password'}
                          defaultValue={systemSettings.sendgrid_api_key || ''}
                          placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">SendGrid Dashboard</a>
                      </p>
                    </div>
                  </div>

                  {/* Twilio Settings */}
                  <div className="space-y-4 p-4 border rounded-lg bg-blue-50">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <Phone className="h-4 w-4 mr-2" />
                      Twilio (SMS Service)
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="twilio_account_sid">Account SID</Label>
                        <Input
                          id="twilio_account_sid"
                          name="twilio_account_sid"
                          type={showApiKeys ? 'text' : 'password'}
                          defaultValue={systemSettings.twilio_account_sid || ''}
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="twilio_auth_token">Auth Token</Label>
                        <Input
                          id="twilio_auth_token"
                          name="twilio_auth_token"
                          type={showApiKeys ? 'text' : 'password'}
                          defaultValue={systemSettings.twilio_auth_token || ''}
                          placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="twilio_phone_number">Twilio Phone Number</Label>
                      <Input
                        id="twilio_phone_number"
                        name="twilio_phone_number"
                        type="text"
                        defaultValue={systemSettings.twilio_phone_number || ''}
                        placeholder="+1234567890"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Format: +1234567890 (include country code)
                      </p>
                    </div>
                    <p className="text-xs text-blue-700">
                      Get your credentials from <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a>
                    </p>
                    
                    {/* Test SMS Button */}
                    <div className="pt-4 border-t border-blue-200">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          const phone = prompt("Enter phone number to test (with country code, e.g., +18282265381):");
                          if (phone) {
                            try {
                              const response = await api.testSMS(phone);
                              if (response.data.success) {
                                toast.success(`Test SMS sent to ${phone}! Check your phone.`);
                              } else {
                                toast.warning(`SMS not sent. Mode: ${response.data.notification_mode}. ${response.data.message}`);
                              }
                            } catch (error) {
                              console.error('Test SMS failed:', error);
                              toast.error(error.response?.data?.detail || 'Failed to send test SMS');
                            }
                          }
                        }}
                        className="w-full"
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Send Test SMS
                      </Button>
                      <p className="text-xs text-gray-600 mt-2">
                        Save settings first, then test. Make sure notification mode is set to "Live" for real SMS.
                      </p>
                    </div>
                  </div>

                  {/* Notification Mode */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h4 className="font-medium text-gray-900">Notification Mode</h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="notification_mode"
                          value="mock"
                          defaultChecked={systemSettings.notification_mode === 'mock'}
                          className="h-4 w-4 text-blue-600"
                        />
                        <div>
                          <span className="font-medium">Mock Mode (Testing)</span>
                          <p className="text-sm text-gray-600">Logs notifications to console without sending</p>
                        </div>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="notification_mode"
                          value="live"
                          defaultChecked={systemSettings.notification_mode === 'live'}
                          className="h-4 w-4 text-blue-600"
                        />
                        <div>
                          <span className="font-medium">Live Mode (Production)</span>
                          <p className="text-sm text-gray-600">Send real emails and SMS notifications</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Show/Hide API Keys Toggle */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="show-api-keys"
                      checked={showApiKeys}
                      onChange={(e) => setShowApiKeys(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="show-api-keys" className="cursor-pointer">
                      Show API keys in plain text
                    </Label>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button
                      type="submit"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Settings
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Database Backup & Restore */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="h-5 w-5 mr-2" />
                  Database Backup & Restore
                </CardTitle>
                <CardDescription>
                  Download a complete backup of your database or restore from a previous backup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Backup Info */}
                {backupInfo && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-3">Current Database Status</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-blue-600 font-medium">Total Documents</p>
                        <p className="text-2xl font-bold text-blue-900">{backupInfo.total_documents}</p>
                      </div>
                      <div>
                        <p className="text-blue-600 font-medium">Users</p>
                        <p className="text-xl font-bold text-blue-900">{backupInfo.collections?.users || 0}</p>
                      </div>
                      <div>
                        <p className="text-blue-600 font-medium">QR Codes</p>
                        <p className="text-xl font-bold text-blue-900">{backupInfo.collections?.qr_codes || 0}</p>
                      </div>
                      <div>
                        <p className="text-blue-600 font-medium">Messages</p>
                        <p className="text-xl font-bold text-blue-900">{backupInfo.collections?.messages || 0}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Backup Section */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Download Backup</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Create a complete backup of your database as a JSON file
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleDownloadBackup}
                      disabled={backupLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {backupLoading ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Download Backup
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Restore Section */}
                <div className="space-y-4 p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-orange-900">Restore from Backup</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        ⚠️ Warning: Restoring will add data from the backup file. Make sure to download a current backup first!
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="restore-file" className="text-gray-900">Select Backup File (JSON)</Label>
                      <input
                        id="restore-file"
                        type="file"
                        accept=".json"
                        onChange={handleRestoreFileChange}
                        className="mt-2 block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
                      />
                      {restoreFile && (
                        <p className="text-sm text-gray-600 mt-2">
                          Selected: {restoreFile.name} ({(restoreFile.size / 1024).toFixed(2)} KB)
                        </p>
                      )}
                    </div>
                    
                    <Button
                      type="button"
                      onClick={handleRestoreBackup}
                      disabled={!restoreFile || backupLoading}
                      variant="outline"
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      {backupLoading ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full mr-2"></div>
                          Restoring...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Restore Database
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>


          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk Generate Dialog */}
      <Dialog open={showBulkGenerateDialog} onOpenChange={setShowBulkGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate QR Code Batch</DialogTitle>
            <DialogDescription>
              Create a batch of QR codes for sticker printing or user purchases
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkGenerate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                name="quantity"
                type="number"
                min="1"
                max="1000"
                placeholder="50"
                required
                data-testid="bulk-quantity-input"
              />
              <p className="text-xs text-gray-500">Maximum 1000 codes per batch</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="purpose">Purpose</Label>
              <select
                id="purpose"
                name="purpose"
                className="w-full p-2 border rounded-md"
                data-testid="bulk-purpose-select"
              >
                <option value="sticker_printing">Sticker Printing</option>
                <option value="user_purchases">User Purchases</option>
                <option value="business_orders">Business Orders</option>
                <option value="testing">Testing</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                name="notes"
                placeholder="e.g., Client order for Restaurant XYZ, Testing new sticker design..."
                rows={3}
                data-testid="bulk-notes-input"
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowBulkGenerateDialog(false)}
                data-testid="bulk-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                data-testid="bulk-submit-btn"
              >
                {loading ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <QrCode className="h-4 w-4 mr-2" />
                    Generate Batch
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Management Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Update product details for the storefront' : 'Add a new physical product to the storefront'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveProduct} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product-name">Product Name *</Label>
                <Input
                  id="product-name"
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                  placeholder="e.g., QR Code Sticker Pack"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="product-price">Price ($) *</Label>
                <Input
                  id="product-price"
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                  placeholder="9.99"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="product-description">Description *</Label>
              <Textarea
                id="product-description"
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                placeholder="Describe the product, its uses, and what's included..."
                rows={3}
                required
              />
            </div>


            <div className="space-y-2">
              <Label htmlFor="product-image">Product Image</Label>
              <div className="space-y-3">
                {productForm.images && productForm.images.length > 0 ? (
                  <div className="relative w-full h-48 border-2 border-gray-300 rounded-lg overflow-hidden">
                    <img 
                      src={productForm.images[0]} 
                      alt="Product preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full">
                    <label htmlFor="product-image-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                        <p className="text-xs text-gray-500">PNG, JPG (MAX. 2MB)</p>
                      </div>
                      <input 
                        id="product-image-upload" 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>


            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product-category">Category</Label>
                <select
                  id="product-category"
                  value={productForm.category}
                  onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="stickers">Stickers</option>
                  <option value="cards">Business Cards</option>
                  <option value="keychains">Keychains</option>
                  <option value="bundles">Bundle Packs</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="qr-codes-included">QR Codes Included *</Label>
                <Input
                  id="qr-codes-included"
                  type="number"
                  value={productForm.qr_codes_included}
                  onChange={(e) => setProductForm({...productForm, qr_codes_included: e.target.value})}
                  placeholder="8"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="stock-quantity">Stock Quantity</Label>
                <Input
                  id="stock-quantity"
                  type="number"
                  value={productForm.stock_quantity}
                  onChange={(e) => setProductForm({...productForm, stock_quantity: e.target.value})}
                  placeholder="Leave empty for unlimited"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is-active"
                checked={productForm.is_active}
                onChange={(e) => setProductForm({...productForm, is_active: e.target.checked})}
                className="w-4 h-4 text-blue-600"
              />
              <Label htmlFor="is-active">Active (visible in storefront)</Label>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowProductDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Subscription Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User Subscription</DialogTitle>
            <DialogDescription>
              Update subscription plan and expiration date for {editingUser?.email || editingUser?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            
            const subscriptionData = {
              subscription_plan: formData.get('subscription_plan'),
              subscription_expires: formData.get('subscription_expires') || null,
              is_active: formData.get('is_active') === 'true'
            };
            
            try {
              await api.updateUserSubscription(editingUser.id, subscriptionData);
              toast.success('User subscription updated successfully!');
              setShowEditUserDialog(false);
              setEditingUser(null);
              fetchUsers();
            } catch (error) {
              console.error('Failed to update user:', error);
              toast.error(error.response?.data?.detail || 'Failed to update user subscription');
            }
          }}>
            <div className="space-y-6">
              {/* Current Info Display */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Current Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium">{editingUser?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Username:</span>
                    <p className="font-medium">{editingUser?.username || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Current Plan:</span>
                    <Badge className="ml-2">{editingUser?.subscription_plan}</Badge>
                  </div>
                  <div>
                    <span className="text-gray-600">QR Codes Used:</span>
                    <p className="font-medium">{editingUser?.qr_codes_used || 0}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Messages Used:</span>
                    <p className="font-medium">{editingUser?.messages_used || 0}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <Badge variant={editingUser?.is_active ? "default" : "destructive"} className="ml-2">
                      {editingUser?.is_active ? "Active" : "Disabled"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Subscription Plan Selection */}
              <div>
                <Label htmlFor="subscription_plan">Subscription Plan</Label>
                <select
                  id="subscription_plan"
                  name="subscription_plan"
                  defaultValue={editingUser?.subscription_plan || 'free'}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                >
                  <option value="free">Free - 10 QR codes, 10 messages</option>
                  <option value="basic">Basic - $2/mo or $20/yr - 50 QR codes, 100 messages, SMS</option>
                  <option value="premium">Premium - $5/mo or $49/yr - 200 QR codes, 200 messages, SMS</option>
                </select>
              </div>

              {/* Expiration Date */}
              <div>
                <Label htmlFor="subscription_expires">Subscription Expiration Date</Label>
                <Input
                  id="subscription_expires"
                  name="subscription_expires"
                  type="datetime-local"
                  defaultValue={editingUser?.subscription_expires ? 
                    new Date(editingUser.subscription_expires).toISOString().slice(0, 16) : 
                    ''
                  }
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for Free plan or set future date for paid plans
                </p>
              </div>

              {/* Active Status */}
              <div>
                <Label htmlFor="is_active">Account Status</Label>
                <select
                  id="is_active"
                  name="is_active"
                  defaultValue={editingUser?.is_active ? 'true' : 'false'}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="true">Active</option>
                  <option value="false">Disabled</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Disabled accounts cannot log in or use the service
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> The user will receive an email notification about their subscription update. 
                  Make sure to set an appropriate expiration date for paid plans.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-6 mt-6 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditUserDialog(false);
                  setEditingUser(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Update Subscription
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}